import axios from "axios";

// ── Access token en mémoire uniquement (jamais localStorage) ─────────────────
let accessToken = null;
export const setAccessToken = (token) => { accessToken = token; };
export const clearAccessToken = () => { accessToken = null; };
export const getAccessToken = () => accessToken;

// ── Proactive refresh : timer déclenché 2 min avant expiry du JWT ─────────────
let _proactiveTimer = null;

export function scheduleProactiveRefresh(token, callback) {
  if (_proactiveTimer) clearTimeout(_proactiveTimer);
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return;
    const delay = payload.exp * 1000 - Date.now() - 2 * 60 * 1000;
    if (delay > 0) {
      _proactiveTimer = setTimeout(callback, delay);
    }
  } catch {}
}

export function cancelProactiveRefresh() {
  if (_proactiveTimer) { clearTimeout(_proactiveTimer); _proactiveTimer = null; }
}

// ── Gestion du refresh concurrent (plusieurs requêtes simultanées en 401) ────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  failedQueue = [];
};

const BASE = process.env.REACT_APP_API_URL || "http://localhost:5243/api";

// Instance dédiée au refresh/logout (sans intercepteur 401 pour éviter la récursion)
export const refreshApi = axios.create({
  baseURL: BASE,
  withCredentials: true,
  timeout: 10000,
});

// Instance principale
const api = axios.create({
  baseURL: BASE,
  headers: {
    "Content-Type": "application/json",
    // Protection CSRF complémentaire : les requêtes cross-origin ne peuvent pas
    // positionner ce header, ce qui bloque les attaques CSRF classiques
    "X-Requested-With": "XMLHttpRequest",
  },
  timeout: 10000,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (process.env.NODE_ENV === "development") {
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
  }
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes("/auth/refresh")
    ) {
      if (process.env.NODE_ENV === "development") {
        console.error("[API Error]", {
          status: error.response?.status,
          url: original?.url,
          data: error.response?.data,
        });
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await refreshApi.post("/auth/refresh");
      setAccessToken(data.token);
      original.headers.Authorization = `Bearer ${data.token}`;
      processQueue(null, data.token);
      return api(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAccessToken();
      cancelProactiveRefresh();
      window.dispatchEvent(new CustomEvent("auth:expired"));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
